<template>
  <e-form ref="formRef">
    <e-select
      v-model="data.ageRules"
      :items="determineAgeMethods"
      :rules="[required()]"
      item-title="text"
      item-value="value"
      label="Правила расчёта возраста туриста"
      class="mb-6"
    />

    <div class="tourists__row">
      <div class="paragraph-l font-600 mr-4">
        <span class="mr-2">Минимальный возраст заезда</span>
        <help-tooltip> Максимальное значение 18 лет.</help-tooltip>
      </div>

      <e-input
        v-model:number-value="data.minAge"
        :rules="[required(), max(18)]"
        :maska="agesMask"
        label="Минимальный возраст"
        class="tourists__input"
        @update:number-value="onMinAgeUpdate"
      />
    </div>
    <div v-if="!noAgeWithNoPlace" class="tourists__row my-4">
      <div class="d-flex align-center">
        <e-checkbox
          v-model="data.ageWithNoPlaceEnabled"
          label="Турист без места"
          class="mr-2"
          :disabled="isDisabledAgeWithNoPlace"
          @update:model-value="onAgeWithNoPlaceEnabledUpdate"
        />
        <help-tooltip>
          <div class="mb-2">Указывается значение, меньше которого турист не имеет места при размещении.</div>
          <div>Максимальное значение 18 лет.</div>
        </help-tooltip>
      </div>

      <e-input
        v-model:number-value="data.ageWithNoPlace"
        :disabled="!data.ageWithNoPlaceEnabled"
        :rules="ageWithNoPlaceRules"
        :maska="agesMask"
        label="Предельный возраст без места"
        class="tourists__input"
      />
    </div>

    <template v-if="!withoutKids">
      <divider class="my-6" />
      <div class="paragraph-l font-600 mb-4">
        Возраст детей
        <help-tooltip class="ml-2">
          <div>
            Значение возраста «С» является включительным. Значение «До» обозначает невключительное значение возраста
            туриста.
          </div>
        </help-tooltip>
      </div>
      <div class="paragraph-xs text-ordinary mb-6">
        Добавьте возрасты детей для больших возможностей тарификации и установления ограничений
      </div>

      <div v-for="(type, index) in data.touristTypes" :key="type.id" class="d-flex mb-4 align-start">
        <e-input
          v-model:number-value="type.from"
          :maska="agesMask"
          :rules="[
            required(),
            data.ageWithNoPlace && index === 0
              ? max(data.ageWithNoPlace, 'Не может быть больше возраста без места')
              : max(18),
            min(data.minAge || 0, 'Не может быть меньше начального возраста'),
          ]"
          :disabled="index !== 0 || !data.ageWithNoPlaceEnabled"
          label="С"
          class="mr-2"
        />
        <e-input
          v-model:number-value="type.to"
          :maska="agesMask"
          :rules="[required(), max(18), min((type.from || 0) + 1, 'Значение должно быть больше возраста от')]"
          label="До"
          class="ml-2"
        />
        <e-btn icon class="ml-2" @click="deleteType(type.id)">
          <e-icon name="trash" class="text-red" />
        </e-btn>
      </div>

      <e-btn
        prepend-icon-src="plus"
        :disabled="isDisabledAddTourist"
        variant="link"
        class="mt-4"
        @click="addTouristsType"
      >
        Добавить Тип туриста
      </e-btn>
    </template>
  </e-form>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import type { EFormType } from '@/core/types/components/components';
import type { PVMTouristsInfo } from '@/parts/tariffs/types/tariffs';
import Divider from '@/core/components/helpers/Divider.vue';
import { EBtn, ECheckbox, EForm, EIcon, EInput, ESelect, HelpTooltip } from '@/core/components/ui';
import { determineAgeMethods } from '@/core/consts/collections';
import { agesMask } from '@/core/consts/masks';
import { isDefined } from '@/core/utils/common';
import { max, min, required } from '@/core/utils/validation';

defineProps<{
  noAgeWithNoPlace?: boolean;
  withoutKids?: boolean;
}>();

const formRef = ref<EFormType>();

const data = defineModel<PVMTouristsInfo>({ required: true });

const ageWithNoPlaceRules = computed(() => {
  if (data.value.ageWithNoPlaceEnabled) {
    return [
      required(),
      max(18),
      min(
        data.value.minAge ? data.value.minAge + 1 : -1,
        'Значение не может быть меньше или равно начального возраста',
      ),
    ];
  }

  return [];
});

const setFirstTouristTypeAgeFrom = () => {
  if (!data.value.touristTypes.length) {
    return;
  }

  if ((isDefined(data.value.minAge) || data.value.minAge === 0) && !data.value.ageWithNoPlaceEnabled) {
    data.value.touristTypes[0]!.from = data.value.minAge || 0;
  }
};

const onMinAgeUpdate = () => {
  if (data.value.minAge === 18) {
    data.value.ageWithNoPlaceEnabled = false;
    data.value.ageWithNoPlace = null;
  }
  setFirstTouristTypeAgeFrom();
};

const onAgeWithNoPlaceEnabledUpdate = (newValue: boolean) => {
  if (!newValue) {
    data.value.ageWithNoPlace = null;
  }
  setFirstTouristTypeAgeFrom();
};

const isDisabledAgeWithNoPlace = toRef(() => data.value.minAge === 18);

const isDisabledAddTourist = toRef(() => {
  const lastTo = data.value.touristTypes.at(-1)?.to;
  return data.value.minAge === 18 || (lastTo && lastTo >= 18);
});

const addTouristsType = () => {
  const type = {
    from: null,
    to: null,
    id: Math.random(),
  };

  data.value.touristTypes.push(type);

  setFirstTouristTypeAgeFrom();
};

const deleteType = (id: number) => {
  data.value.touristTypes = data.value.touristTypes.filter((type) => type.id !== id);

  setFirstTouristTypeAgeFrom();
};

watch(
  () => data.value.touristTypes,
  () => {
    data.value.touristTypes.forEach((type, index) => {
      if (index === 0) {
        return;
      }

      const prevAge = data.value.touristTypes[index - 1].to;

      type.from = +prevAge!;
    });
  },
  {
    deep: true,
  },
);

defineExpose({
  validate: () => formRef.value?.validate(),
});
</script>

<style scoped lang="scss">
.tourists {
  &__input {
    max-width: 280px;

    @include extra-xs {
      max-width: 100%;
    }
  }

  &__row {
    display: flex;
    justify-content: space-between;

    @include extra-xs {
      flex-direction: column;

      .tourists__input {
        margin-top: $gap-5;
      }
    }
  }
}
</style>
